import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import config from 'config';
import { store } from 'store/store';
import {
  Hashtag,
  MentionRequestItem,
  MyProfile,
  PaginatedResponse,
  PublicUserProfileWithSocialStatus,
} from 'types';
import { api } from './api';
import { graphsApi } from './graphsApi';
import { socialApi } from './socialApi';
import { contentApi } from './contentApi';
import { toast } from 'components/Toast';
import {
  PostImageMultipartInitiateRequest,
  PostImageMultipartInitiateResponse,
  PostMediaMultipartCompleteRequest,
  PostVideoMultipartInitiateResponse,
  PostVideoMultipartInitiateRequest,
  CreatePostResponse,
  PostMediaMultipartAbortRequest,
  CreatePostRequest,
} from '@/features/upload/types';
import { removeMyPostFromCached } from '../cache-updates/contentApi';
import { PostFeedItem } from '@/features/posts/types';

export interface RefreshProps {
  refreshToken: string;
}

export interface LogoutProps extends RefreshProps {}

interface VerifyUserEmailProps {
  influencerId: string;
  otpCode: string;
}

export interface MachineTokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
}

interface GetHashtagListProps {
  keyword: string;
  offset: number;
  limit: number;
}

interface DeletePostProps {
  creatorsContentId: string;
  visibility: PostFeedItem['visibility'];
}

interface UpdateCaptionProps {
  creatorsContentId: string;
  caption: string;
  mentionRequest: MentionRequestItem[];
}

interface DismissSuggestedProfileProps {
  dismissId: string;
  type?: 'creator' | 'citizen';
  citizenId?: string;
}

const USERS_API = config.usersAPI;

export const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMyProfile: build.query<MyProfile, void>({
      async queryFn(_, _queryApi, _extraOptions, baseQuery) {
        const myProfileRes = await baseQuery({
          url: `${USERS_API}/influencers/me`,
          headers: { 'x-api-version': '4.0' },
          withAccessToken: true,
        });

        if (myProfileRes.error) {
          return { error: myProfileRes.error as FetchBaseQueryError };
        }

        const { data: socialOverview } = await _queryApi.dispatch(
          graphsApi.endpoints.getMyProfileSocialInfo.initiate()
        );

        return { data: { ...(myProfileRes.data as {}), ...socialOverview } as MyProfile };
      },
      providesTags: ['UserProfile'],
    }),
    getFollowersCount: build.query<number, string>({
      query: (influencerId) => ({
        url: `${USERS_API}/influencers/me/followers-count/${influencerId}`,
        withAccessToken: true,
      }),
      transformResponse: (response: { count: number }) => response.count,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: followersCount } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData('getMyProfile', undefined, (draft) => {
              Object.assign(draft, {
                ...draft,
                followersCount,
              });
            })
          );
        } catch {}
      },
    }),
    getPublicProfileAsLoggedInUser: build.query<PublicUserProfileWithSocialStatus, string>({
      async queryFn(username, _queryApi, _extraOptions, baseQuery) {
        const profileRes = await baseQuery({
          url: `${USERS_API}/influencers/profile/${username}`,
          withAccessToken: true,
        });

        if (profileRes.error) {
          return { error: profileRes.error as FetchBaseQueryError };
        }

        const { data: socialOverview } = await _queryApi.dispatch(
          socialApi.endpoints.getProfileSocialInfo.initiate(
            (profileRes.data as PublicUserProfileWithSocialStatus)?.id
          )
        );

        return {
          data: {
            ...(profileRes.data as {}),
            ...socialOverview,
          } as PublicUserProfileWithSocialStatus,
        };
      },
      providesTags: ['UserProfile'],
    }),
    deletePost: build.mutation<void, DeletePostProps>({
      query: ({ creatorsContentId }) => ({
        url: `${USERS_API}/creators-content/${creatorsContentId}`,
        method: 'DELETE',
        headers: { 'x-api-version': '3.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ creatorsContentId, visibility }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          removeMyPostFromCached({
            dispatch,
            postId: creatorsContentId,
            visibility,
          });
          toast.success('The post was successfully deleted');
        } catch {
          toast.error('Sorry, something went wrong');
        }
      },
    }),
    updateCaption: build.mutation<
      { caption: string; mentionRequest: MentionRequestItem[] },
      UpdateCaptionProps
    >({
      query: ({ caption, mentionRequest, creatorsContentId }) => ({
        url: `${USERS_API}/creators-content/${creatorsContentId}/caption`,
        method: 'POST',
        body: {
          caption,
          mentionRequest,
        },
        headers: { 'x-api-version': '4.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ creatorsContentId }, { dispatch, queryFulfilled }) {
        try {
          const { data: newCaptionResponse } = await queryFulfilled;

          dispatch(
            contentApi.util.updateQueryData('getPostAsLoggedInUser', creatorsContentId, (draft) => {
              Object.assign(draft, {
                ...draft,
                caption: {
                  content: newCaptionResponse.caption,
                  mentions: newCaptionResponse.mentionRequest,
                },
              });
            })
          );
          toast.success('The caption of the post was succesfully updated.');
        } catch {
          toast.error('Sorry, something went wrong');
        }
      },
    }),
    verifyUserEmail: build.mutation<void, VerifyUserEmailProps>({
      query: ({ influencerId, otpCode }) => ({
        url: `${USERS_API}/influencers/verify-influencer-email`,
        method: 'POST',
        body: {
          influencerId,
          otpCode,
        },
      }),
    }),
    getHashtagList: build.query<PaginatedResponse<Hashtag>, GetHashtagListProps>({
      query: ({ keyword, offset, limit }) => ({
        url: `${USERS_API}/hashtags?search=${encodeURIComponent(
          keyword
        )}&limit=${limit}&offset=${offset}`,
        method: 'GET',
        headers: { 'x-api-version': '3.0' },
        withAccessToken: true,
      }),
      transformResponse: (response: any, _, { offset, limit }) => {
        const updatedResponse = {
          ...response,
          total: response.totalHashTags,
          page: offset / limit + 1,
        };
        delete updatedResponse.totalHashTags;
        return updatedResponse;
      },
    }),
    createPost: build.mutation<CreatePostResponse, CreatePostRequest>({
      query: ({ mediaType, caption, sharingOptions, externalLink, mentionRequest }) => ({
        url: `${USERS_API}/v7/creators-content`,
        method: 'POST',
        body: {
          mediaType,
          externalLink,
          caption,
          mentionRequest,
          sharingOptions,
        },
        withAccessToken: true,
      }),
    }),
    initiatePostImageMultipartUpload: build.mutation<
      PostImageMultipartInitiateResponse,
      PostImageMultipartInitiateRequest
    >({
      query: ({ creatorsContentId, ...rest }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/image-upload/initiate`,
        method: 'POST',
        body: {
          ...rest,
        },
        withAccessToken: true,
      }),
    }),
    initiatePostVideoCoverImageMultipartUpload: build.mutation<
      PostImageMultipartInitiateResponse,
      PostImageMultipartInitiateRequest
    >({
      query: ({ creatorsContentId, ...rest }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/video-cover-image-upload/initiate`,
        method: 'POST',
        body: {
          ...rest,
        },
        withAccessToken: true,
      }),
    }),
    initiatePostVideoMultipartUpload: build.mutation<
      PostVideoMultipartInitiateResponse,
      PostVideoMultipartInitiateRequest
    >({
      query: ({ creatorsContentId, fileName, fileSize }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/video-upload/initiate`,
        method: 'POST',
        body: {
          fileName,
          fileSize,
        },
        withAccessToken: true,
      }),
    }),

    completePostImageMultipartUpload: build.mutation<void, PostMediaMultipartCompleteRequest>({
      query: ({ creatorsContentId, uploadId }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/image-upload/complete`,
        body: {
          uploadId,
        },
        method: 'POST',
        withAccessToken: true,
      }),
    }),
    completePostVideoCoverImageMultipartUpload: build.mutation<
      void,
      PostMediaMultipartCompleteRequest
    >({
      query: ({ creatorsContentId, uploadId }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/video-cover-image-upload/complete`,
        body: {
          uploadId,
        },
        method: 'POST',
        withAccessToken: true,
      }),
    }),
    completePostVideoMultipartUpload: build.mutation<void, PostMediaMultipartCompleteRequest>({
      query: ({ creatorsContentId, uploadId }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/video-upload/complete`,
        body: {
          uploadId,
        },
        method: 'POST',
        withAccessToken: true,
      }),
    }),
    abortPostImageMultipartUpload: build.mutation<void, PostMediaMultipartAbortRequest>({
      query: ({ creatorsContentId, uploadId }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/image-upload/abort/`,
        method: 'POST',
        body: {
          uploadId,
        },
        withAccessToken: true,
        keepalive: true,
      }),
    }),
    abortPostCoverImageMultipartUpload: build.mutation<void, PostMediaMultipartAbortRequest>({
      query: ({ creatorsContentId, uploadId }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/video-cover-image-upload/abort/`,
        method: 'POST',
        body: {
          uploadId,
        },
        withAccessToken: true,
        keepalive: true,
      }),
    }),
    abortPostVideoMultipartUpload: build.mutation<void, PostMediaMultipartAbortRequest>({
      query: ({ creatorsContentId, uploadId }) => ({
        url: `${USERS_API}/v1/creators-content/${creatorsContentId}/video-upload/abort/`,
        method: 'POST',
        body: {
          uploadId,
        },
        withAccessToken: true,
        keepalive: true,
      }),
    }),
    getPostUploadProcessingStatus: build.query<{ status: string }, { creatorsContentId: string }>({
      query: ({ creatorsContentId }) => ({
        url: `${USERS_API}/creators-content/status/${creatorsContentId}`,
        withAccessToken: true,
      }),
      providesTags: ['Upload_status'],
    }),
    dismissSuggestion: build.mutation<void, DismissSuggestedProfileProps>({
      query: ({ dismissId }) => ({
        url: `${USERS_API}/influencers/me/dismiss-relevant-profile-suggestion`,
        method: 'POST',
        body: {
          suggestedProfileId: dismissId,
        },
        withAccessToken: true,
      }),
      async onQueryStarted({ dismissId, type, citizenId }, { dispatch, queryFulfilled }) {
        if (type) {
          const updatedSuggestionList = dispatch(
            graphsApi.util.updateQueryData(
              type === 'creator' ? 'getRelevantCreators' : 'getRelevantCitizens',
              { offset: 0, limit: 8, citizenId },
              (previousSuggestedCitizens) => {
                return {
                  ...previousSuggestedCitizens,
                  total: previousSuggestedCitizens.total - 1,
                  data: previousSuggestedCitizens.data.filter(
                    (suggestion) => suggestion.citizenId ?? suggestion.id !== dismissId
                  ),
                };
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            updatedSuggestionList.undo();
          }
        }
      },
    }),
    likeCreatorsContent: build.mutation<void, string>({
      query: (creatorsContentId) => ({
        url: `${USERS_API}/influencers/me/liked-creators-content/${creatorsContentId}`,
        method: 'POST',
        headers: { 'x-api-version': '3.0' },
        withAccessToken: true,
      }),
      async onQueryStarted(creatorsContentId, { dispatch, queryFulfilled }) {
        const updatePostInfo = dispatch(
          contentApi.util.updateQueryData('getPostAsLoggedInUser', creatorsContentId, (draft) => {
            Object.assign(draft, {
              ...draft,
              liked: true,
              likesCount: draft.likesCount + 1,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updatePostInfo.undo();
        }
      },
      invalidatesTags: ['UserProfile'],
    }),
    unlikeCreatorsContent: build.mutation<void, string>({
      query: (creatorsContentId) => ({
        url: `${USERS_API}/influencers/me/un-liked-creators-content/${creatorsContentId}`,
        method: 'POST',
        headers: { 'x-api-version': '3.0' },
        withAccessToken: true,
      }),
      async onQueryStarted(creatorsContentId, { dispatch, queryFulfilled }) {
        const updatePostInfo = dispatch(
          contentApi.util.updateQueryData('getPostAsLoggedInUser', creatorsContentId, (draft) => {
            Object.assign(draft, {
              ...draft,
              liked: false,
              likesCount: draft.likesCount > 0 ? draft.likesCount - 1 : draft.likesCount,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updatePostInfo.undo();
        }
      },
      invalidatesTags: ['UserProfile'],
    }),
    followCreator: build.mutation<void, { citizenId: string; citizenUsername: string }>({
      query: ({ citizenId }) => ({
        url: `${USERS_API}/influencers/me/follow/${citizenId}`,
        method: 'POST',
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
        const updateUserProfile = dispatch(
          usersApi.util.updateQueryData(
            'getPublicProfileAsLoggedInUser',
            citizenUsername,
            (draft) => {
              Object.assign(draft, {
                ...draft,
                amIFollowing: true,
              });
            }
          )
        );
        const updateMyProfile = dispatch(
          usersApi.util.updateQueryData('getMyProfile', undefined, (draft) => {
            Object.assign(draft, {
              ...draft,
              followingCount: draft.followingCount + 1,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updateUserProfile.undo();
          updateMyProfile.undo();
        }
      },
    }),
    unfollowCreator: build.mutation<void, { citizenId: string; citizenUsername: string }>({
      query: ({ citizenId }) => ({
        url: `${USERS_API}/influencers/me/unfollow/${citizenId}`,
        method: 'POST',
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
        const updateUserProfile = dispatch(
          usersApi.util.updateQueryData(
            'getPublicProfileAsLoggedInUser',
            citizenUsername,
            (draft) => {
              Object.assign(draft, {
                ...draft,
                amIFollowing: false,
              });
            }
          )
        );
        const updateMyProfile = dispatch(
          usersApi.util.updateQueryData('getMyProfile', undefined, (draft) => {
            Object.assign(draft, {
              ...draft,
              followingCount: draft.followersCount
                ? draft.followingCount - 1
                : draft.followersCount,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updateUserProfile.undo();
          updateMyProfile.undo();
        }
      },
    }),
    addFriend: build.mutation<
      { friendRequestId: string },
      { citizenId: string; citizenUsername: string }
    >({
      query: ({ citizenId }) => ({
        url: `${USERS_API}/influencers/me/friend-request/${citizenId}`,
        method: 'POST',
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
        const updateUserProfile = dispatch(
          usersApi.util.updateQueryData(
            'getPublicProfileAsLoggedInUser',
            citizenUsername,
            (draft) => {
              Object.assign(draft, {
                ...draft,
                friendRequestDetails: {
                  isOutFriendRequest: true,
                  status: 'requested',
                },
              });
            }
          )
        );
        try {
          const response = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData(
              'getPublicProfileAsLoggedInUser',
              citizenUsername,
              (draft) => {
                Object.assign(draft, {
                  ...draft,
                  friendRequestDetails: {
                    ...draft.friendRequestDetails,
                    id: response.data.friendRequestId,
                  },
                });
              }
            )
          );
        } catch {
          updateUserProfile.undo();
        }
      },
    }),
    unfriend: build.mutation<void, { citizenId: string; citizenUsername: string }>({
      query: ({ citizenId }) => ({
        url: `${USERS_API}/influencers/me/unfriend/${citizenId}`,
        method: 'POST',
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
        const updateUserProfile = dispatch(
          usersApi.util.updateQueryData(
            'getPublicProfileAsLoggedInUser',
            citizenUsername,
            (draft) => {
              Object.assign(draft, {
                ...draft,
                isFriends: false,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          updateUserProfile.undo();
        }
      },
    }),
    cancelFriendRequest: build.mutation<void, { friendRequestId: string; citizenUsername: string }>(
      {
        query: ({ friendRequestId }) => ({
          url: `${USERS_API}/influencers/me/cancel-friend-request/${friendRequestId}`,
          method: 'POST',
          headers: { 'x-api-version': '2.0' },
          withAccessToken: true,
        }),
        async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
          const updateUserProfile = dispatch(
            usersApi.util.updateQueryData(
              'getPublicProfileAsLoggedInUser',
              citizenUsername,
              (draft) => {
                delete draft['friendRequestDetails'];
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            updateUserProfile.undo();
          }
        },
      }
    ),
    approveFriendRequest: build.mutation<
      void,
      { friendRequestId: string; citizenUsername: string }
    >({
      query: ({ friendRequestId }) => ({
        url: `${USERS_API}/influencers/me/approve-friend-request/${friendRequestId}`,
        method: 'POST',
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
        const updateUserProfile = dispatch(
          usersApi.util.updateQueryData(
            'getPublicProfileAsLoggedInUser',
            citizenUsername,
            (draft) => {
              delete draft['friendRequestDetails'];
              Object.assign(draft, {
                ...draft,
                isFriends: true,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          updateUserProfile.undo();
        }
      },
    }),
    declineFriendRequest: build.mutation<
      void,
      { friendRequestId: string; citizenUsername: string }
    >({
      query: ({ friendRequestId }) => ({
        url: `${USERS_API}/influencers/me/decline-friend-request/${friendRequestId}`,
        method: 'POST',
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      async onQueryStarted({ citizenUsername }, { dispatch, queryFulfilled }) {
        const updateUserProfile = dispatch(
          usersApi.util.updateQueryData(
            'getPublicProfileAsLoggedInUser',
            citizenUsername,
            (draft) => {
              delete draft['friendRequestDetails'];
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          updateUserProfile.undo();
        }
      },
    }),
  }),
});

export const {
  useVerifyUserEmailMutation,
  useCreatePostMutation,
  useGetHashtagListQuery,
  useInitiatePostImageMultipartUploadMutation,
  useInitiatePostVideoCoverImageMultipartUploadMutation,
  useInitiatePostVideoMultipartUploadMutation,
  useCompletePostImageMultipartUploadMutation,
  useCompletePostVideoCoverImageMultipartUploadMutation,
  useCompletePostVideoMultipartUploadMutation,
  useAbortPostImageMultipartUploadMutation,
  useAbortPostCoverImageMultipartUploadMutation,
  useAbortPostVideoMultipartUploadMutation,
  useGetPostUploadProcessingStatusQuery,
  useGetMyProfileQuery,
  useGetPublicProfileAsLoggedInUserQuery,
  useGetFollowersCountQuery,
  useUpdateCaptionMutation,
  useDeletePostMutation,
  useDismissSuggestionMutation,
  useLikeCreatorsContentMutation,
  useUnlikeCreatorsContentMutation,
  useFollowCreatorMutation,
  useUnfollowCreatorMutation,
  useAddFriendMutation,
  useCancelFriendRequestMutation,
  useApproveFriendRequestMutation,
  useDeclineFriendRequestMutation,
  useUnfriendMutation,
  util: { getRunningQueriesThunk },
} = usersApi;
